/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { combineLatest, EMPTY, switchMap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { acdStatusChange, requestedAcdStatusChange } from '../+state/user.actions';
import { selectComputedAgentStatusWithReason } from '../../call/+state/call.selectors';
import { AgentStatus, AgentStatusRecord } from 'CalltakingCoreApi';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { selectIsDeviceConnectedAndHeadsetDisconnected } from '../../call/+state/cchub.selectors';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { Topic } from '../model/multiple-subscriptions';
import { selectIsPrimaryConnectionActive } from '../../call/+state/media.selectors';
import { RequestedAgentStatus } from '../model/requested-agent-status';
import { selectAgentStatusRecord, selectSystemReasonCodes, selectSystemReasonCodesMap } from '../+state/user.selectors';
import { normalizeComputedAgentStatus, SYSTEM_REASON_CODE } from '../util/system-reason-code.util';
import { EnvironmentService } from '../../core/services/environment.service';

@Injectable({
    providedIn: 'root'
})
export class AcdStatusService extends StompBaseConsumer {
    private readonly API_BASE_URL = '/api/acd/v1/';

    constructor(
        private readonly http: HttpClient,
        private readonly clusterUrlProviderService: ClusterUrlProviderService,
        protected store: Store,
        protected stompManager: StompManagerService,
        private readonly env: EnvironmentService
    ) {
        super(stompManager, store);
            let active$ = this.store.select(selectIsPrimaryConnectionActive);
            active$
                .pipe(switchMap((active) => (active ? this.store.select(selectComputedAgentStatusWithReason).pipe(filter((val) => !!val)) : EMPTY)))
                .subscribe((requestedAgentStatus) => this.store.dispatch(acdStatusChange({requestedAgentStatus: requestedAgentStatus})));
            active$
                .pipe(
                    switchMap((active) => (active ? this.store.select(selectIsDeviceConnectedAndHeadsetDisconnected).pipe(filter((val) => !!val)) : EMPTY)),
                    filter((val) => !!val),
                    withLatestFrom(this.store.select(selectSystemReasonCodesMap)),
                    map(([, systemReasonCodesMap]) =>
                        systemReasonCodesMap[SYSTEM_REASON_CODE.noHeadset.code]?.effectiveLabel || 'No headset'
                    )
                )
                .subscribe((statusReasonDisplayName) => this.store.dispatch(requestedAcdStatusChange({ agentStatus: 'NOT_READY', statusReasonDisplayName })));
    }

    protected getTopics(): Topic[] {
        return [Topic.REQUEST_USER_STATUS];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any, clusterName: string) => void } {
        return {
            [Topic.REQUEST_USER_STATUS]: this.handleRequestUserStatusMessage.bind(this)
        };
    }

    protected handleRequestUserStatusMessage(agentStatus: AgentStatus, clusterName: string) {
        this.handleRequestStatusUpdate(agentStatus, clusterName);
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD status topic ${topic} subscription success: ${receiptId}`);
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD status topic ${topic} subscription end: ${receiptId}`);
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD status topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    private handleRequestStatusUpdate(agentStatus: AgentStatus, clusterName: string) {
        combineLatest([this.store.select(selectAgentStatusRecord), this.store.select(selectSystemReasonCodes)])
            .pipe(take(1))
            .subscribe(([currentAgentStatus, systemReasonCodes]) => {
                const requestedAgentStatus = normalizeComputedAgentStatus(
                    { status: agentStatus, clusterName: clusterName, isAgentRequested: false },
                    currentAgentStatus,
                    systemReasonCodes
                );
                this.store.dispatch(acdStatusChange({ requestedAgentStatus }));
            });
    }

    public reportAgentStatus(requestedAgentStatus: RequestedAgentStatus) {
        console.log(`Reporting agent status ${requestedAgentStatus.status} for cluster ${requestedAgentStatus.clusterName} with reason ${requestedAgentStatus.statusReasonDisplayName} and reasonId ${requestedAgentStatus.statusReasonId}`);
        let params = new HttpParams();
        const nrrcEnabled = this.env.isNrrcGloballyEnabled();
        const statusReasonDisplayName = nrrcEnabled || requestedAgentStatus.statusReasonDisplayName === 'No Answer'
            ? requestedAgentStatus.statusReasonDisplayName
            : undefined;
        params = requestedAgentStatus.isAgentRequested ? params.append('agentRequested', requestedAgentStatus.isAgentRequested) : params;
        params = statusReasonDisplayName ? params.append('statusReasonDisplayName', statusReasonDisplayName) : params;
        params = nrrcEnabled && requestedAgentStatus.statusReasonId ? params.append('statusReasonId', requestedAgentStatus.statusReasonId) : params;
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(requestedAgentStatus.clusterName, `${this.API_BASE_URL}report-status/${requestedAgentStatus.status}`);
        return this.http.post<AgentStatusRecord>(url, {}, { params: params });
    }
}
